/**
 * EventTypeRoleEditor Component
 *
 * Inline editor for managing template roles within an event type.
 * Supports adding, editing, deleting, and drag-to-reorder.
 */

import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EventTypeRole } from '../../api/eventTypes';

export interface RoleInput {
  /**
   * Stable identity for React keys and dnd-kit, assigned once when the row is
   * created. It must live ON the role rather than in a side table keyed by
   * object identity: every edit replaces the role object, and a key derived
   * from that identity would change on every keystroke, remounting the input
   * and dropping focus.
   */
  uid: string;
  id?: string;
  roleName: string;
  requiredCount: number;
  description: string;
}

let uidCounter = 0;
const nextUid = (): string => `role-${++uidCounter}`;

interface EventTypeRoleEditorProps {
  roles: RoleInput[];
  onChange: (roles: RoleInput[]) => void;
  disabled?: boolean;
}

export default function EventTypeRoleEditor({
  roles,
  onChange,
  disabled = false,
}: EventTypeRoleEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // A freshly added row lands below the fold once the list is a few roles
  // long, so scroll it into the modal's viewport and put the caret in it —
  // otherwise "+ Add Role" looks like it did nothing.
  const nameInputs = useRef(new Map<string, HTMLInputElement>());
  const [pendingFocusUid, setPendingFocusUid] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingFocusUid) return;
    const input = nameInputs.current.get(pendingFocusUid);
    setPendingFocusUid(null);
    if (!input) return;
    // Guarded: jsdom/happy-dom don't implement scrollIntoView.
    input.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    input.focus();
  }, [pendingFocusUid, roles]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAddRole = () => {
    const uid = nextUid();
    onChange([...roles, { uid, roleName: '', requiredCount: 1, description: '' }]);
    setEditingIndex(roles.length);
    setPendingFocusUid(uid);
  };

  const handleUpdateRole = (index: number, field: keyof RoleInput, value: string | number) => {
    const updated = [...roles];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleDeleteRole = (index: number) => {
    onChange(roles.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = roles.map((r) => r.uid);
    const oldIndex = ids.indexOf(String(e.active.id));
    const newIndex = ids.indexOf(String(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(roles, oldIndex, newIndex));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Template Roles
        </label>
        {!disabled && (
          <button
            type="button"
            onClick={handleAddRole}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            + Add Role
          </button>
        )}
      </div>

      {roles.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No roles defined. Add roles to create a template.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={roles.map((r) => r.uid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {roles.map((role, index) => (
                <SortableRoleItem key={role.uid} id={role.uid} disabled={disabled}>
                  {(handleProps) => (
                    <>
                      {/* Drag handle */}
                      <button
                        type="button"
                        aria-label="Drag to reorder"
                        disabled={disabled}
                        className="self-stretch px-1 -ml-1 flex items-center text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-rack rounded touch-none disabled:opacity-30 disabled:cursor-not-allowed"
                        {...handleProps.attributes}
                        {...handleProps.listeners}
                        ref={handleProps.setActivatorNodeRef}
                      >
                        <GripIcon className="w-4 h-4" />
                      </button>

                      {/* Role fields */}
                      <div className="flex-1 grid grid-cols-12 gap-2">
                        <div className="col-span-5">
                          <input
                            type="text"
                            ref={(el) => {
                              if (el) nameInputs.current.set(role.uid, el);
                              else nameInputs.current.delete(role.uid);
                            }}
                            value={role.roleName}
                            onChange={(e) => handleUpdateRole(index, 'roleName', e.target.value)}
                            placeholder="Role name"
                            disabled={disabled}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-rack focus:border-rack disabled:bg-gray-100"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            min={1}
                            max={50}
                            value={role.requiredCount}
                            onChange={(e) => handleUpdateRole(index, 'requiredCount', parseInt(e.target.value) || 1)}
                            disabled={disabled}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-rack focus:border-rack disabled:bg-gray-100"
                            title="Number needed"
                          />
                        </div>
                        <div className="col-span-5">
                          <input
                            type="text"
                            value={role.description}
                            onChange={(e) => handleUpdateRole(index, 'description', e.target.value)}
                            placeholder="Description (optional)"
                            disabled={disabled}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-rack focus:border-rack disabled:bg-gray-100"
                          />
                        </div>
                      </div>

                      {/* Delete button */}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => handleDeleteRole(index)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove role"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                </SortableRoleItem>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {roles.length > 0 && (
        <p className="text-xs text-gray-500">
          These roles will be automatically added when creating events with this template.
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Helper function to convert API roles to editor format
// =============================================================================

export function apiRolesToEditorRoles(roles: EventTypeRole[]): RoleInput[] {
  return roles.map((role) => ({
    uid: nextUid(),
    id: role.id,
    roleName: role.roleName,
    requiredCount: role.requiredCount,
    description: role.description || '',
  }));
}

/**
 * Same conversion, but the rows are detached from their source records.
 * Duplicating a template creates brand-new roles, so carrying the originals'
 * `id` would misrepresent them as already-saved rows of the new template.
 */
export function apiRolesToNewEditorRoles(roles: EventTypeRole[]): RoleInput[] {
  return apiRolesToEditorRoles(roles).map(({ id: _id, ...role }) => role);
}

export function editorRolesToApiRoles(roles: RoleInput[]): Array<{
  roleName: string;
  requiredCount: number;
  description: string | null;
  sortOrder: number;
}> {
  return roles.map((role, index) => ({
    roleName: role.roleName,
    requiredCount: role.requiredCount,
    description: role.description || null,
    sortOrder: index,
  }));
}

// =============================================================================
// Sortable item wrapper
// =============================================================================

interface SortableHandleProps {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
}

function SortableRoleItem({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled: boolean;
  children: (handle: SortableHandleProps) => React.ReactNode;
}) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
    setActivatorNodeRef,
  } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : undefined,
    background: isDragging ? '#ffffff' : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
    >
      {children({ attributes, listeners, setActivatorNodeRef })}
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function GripIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <circle cx="7" cy="5" r="1.5" />
      <circle cx="13" cy="5" r="1.5" />
      <circle cx="7" cy="10" r="1.5" />
      <circle cx="13" cy="10" r="1.5" />
      <circle cx="7" cy="15" r="1.5" />
      <circle cx="13" cy="15" r="1.5" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
